import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaHammer, FaTree, FaHome, FaBuilding, FaPencilRuler, FaTools, FaTimes, FaWhatsapp, FaPhone } from 'react-icons/fa';
import './ConstructionDivision.css';

const services = [
  {
    icon: <FaTree />,
    title: 'Custom Woodworks',
    description: 'Handcrafted furniture, cabinetry, and wood installations with premium materials and finishes.',
    details: {
      overview: 'We craft bespoke wooden pieces tailored to your space — from living room furniture to full kitchen cabinetry. Every piece is handmade by our skilled carpenters using the finest timber.',
      materials: ['Teak', 'Mahogany', 'Pine', 'Plywood (Marine Grade)', 'MDF', 'Hardwood veneer'],
      includes: [
        'Custom design consultation',
        'Material selection & samples',
        'Handcrafted manufacturing',
        'On-site installation',
        'Finishing & polishing',
        '1-year workmanship warranty',
      ],
      timeline: '2 – 6 weeks depending on scope',
    },
  },
  {
    icon: <FaHome />,
    title: 'Renovations',
    description: 'Complete home and commercial renovations — kitchens, bathrooms, floors, and full interior makeovers.',
    details: {
      overview: 'Whether it\'s a single room refresh or a full property renovation, we handle everything from strip-out to final coat of paint. Minimal disruption, maximum transformation.',
      materials: ['Ceramic & porcelain tiles', 'Vinyl & laminate flooring', 'Gypsum board', 'PVC ceilings', 'Paint & texture finishes', 'Granite & quartz countertops'],
      includes: [
        'Site assessment & planning',
        'Demolition & strip-out',
        'Plumbing & electrical liaison',
        'Tiling, flooring & plastering',
        'Painting & decorative finishes',
        'Final clean & punch-list walkthrough',
      ],
      timeline: '1 – 8 weeks depending on scale',
    },
  },
  {
    icon: <FaBuilding />,
    title: 'Full Construction',
    description: 'End-to-end residential and commercial construction from foundation to finishing.',
    details: {
      overview: 'From laying the foundation to handing over the keys, we manage every phase of your build. We work with architects, engineers, and suppliers to deliver on time and on budget.',
      materials: ['Reinforced concrete', 'Structural steel', 'AAC / hollow blocks', 'Roofing sheets & tiles', 'Waterproofing membrane', 'Imported & local finishing materials'],
      includes: [
        'Design & BOQ preparation',
        'Foundation & structural work',
        'Masonry & brickwork',
        'Roofing & waterproofing',
        'Electrical & plumbing rough-in',
        'Interior & exterior finishing',
        'Handover with full documentation',
      ],
      timeline: '3 – 18 months depending on size',
    },
  },
  {
    icon: <FaPencilRuler />,
    title: 'Interior Design',
    description: 'Modern interior design concepts that transform spaces into stunning living environments.',
    details: {
      overview: 'Our interior designers blend aesthetics with functionality. We create mood boards, 3D visualizations, and full fit-out plans so you can see your space before a single nail is driven.',
      materials: ['Decorative wallpapers', 'LED cove & strip lighting', 'False ceiling systems', 'Upholstery fabrics', 'Decorative glass & mirrors', 'Premium paint brands'],
      includes: [
        'Space planning & concept design',
        '3D rendering & mood board',
        'Material & colour scheme selection',
        'Furniture sourcing & coordination',
        'Full fit-out supervision',
        'Styling & accessory placement',
      ],
      timeline: '1 – 4 weeks design + fit-out',
    },
  },
  {
    icon: <FaHammer />,
    title: 'Structural Work',
    description: 'Reinforced concrete works, roofing, and structural modifications with safety compliance.',
    details: {
      overview: 'Structural integrity is non-negotiable. Our team handles reinforced concrete work, column extensions, beam repairs, roofing systems, and any modifications that require certified structural assessment.',
      materials: ['High-grade cement & aggregates', 'TMT rebar steel', 'Formwork (steel & timber)', 'Asphalt roof shingles', 'Galvanized roofing sheets', 'Structural welding consumables'],
      includes: [
        'Structural assessment & load calculation',
        'Shuttering & reinforcement work',
        'Concrete pouring & curing',
        'Roofing frame & sheet installation',
        'Post-construction inspection',
        'Safety compliance documentation',
      ],
      timeline: '2 – 12 weeks depending on scope',
    },
  },
  {
    icon: <FaTools />,
    title: 'Maintenance',
    description: 'Ongoing maintenance services for residential and commercial properties across Sri Lanka.',
    details: {
      overview: 'Keep your property in top condition with our scheduled and on-call maintenance services. We cover everything from minor repairs to preventive upkeep programs for commercial facilities.',
      materials: ['Waterproofing compounds', 'Sealants & adhesives', 'Replacement tiles & fixtures', 'Paint touch-up materials', 'Electrical & plumbing spares', 'Cleaning & treatment chemicals'],
      includes: [
        'Periodic inspection visits',
        'Plumbing leak detection & repair',
        'Electrical fault diagnosis',
        'Roof leak repairs',
        'Wall crack sealing & repainting',
        'Annual maintenance contract option',
      ],
      timeline: 'Ongoing / as scheduled',
    },
  },
];

const ConstructionDivision = () => {
  const [ref, inView] = useInView({ threshold: 0.05, triggerOnce: true });
  const [selected, setSelected] = useState(null);

  return (
    <>
    <section className="construction" id="construction" ref={ref}>
      <div className="construction__bg-text">BUILD</div>
      <div className="construction__container">
        <motion.div
          className="construction__header"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <div className="construction__header-content">
            <span className="section-tag">Constructions Division</span>
            <h2 className="section-title">
              Craftsmanship That
              <br />
              <span className="text-green">Stands the Test of Time</span>
            </h2>
            <p className="section-description">
              Construction by KAT99 Pvt Ltd — we specialize in woodworks,
              renovations, and full-scale construction projects across Sri Lanka.
              High-quality craftsmanship, timely completion, and your vision made real.
            </p>
          </div>
          <motion.div
            className="construction__header-badge"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="construction__badge-inner">
              <span className="construction__badge-label">KAT99</span>
              <span className="construction__badge-sub">Constructions</span>
            </div>
          </motion.div>
        </motion.div>

        <div className="construction__grid">
          {services.map((service, i) => (
            <motion.div
              key={i}
              className="construction__card"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 + i * 0.08 }}
              onClick={() => setSelected(service)}
              role="button"
              style={{ cursor: 'pointer' }}
            >
              <div className="construction__card-number">
                {String(i + 1).padStart(2, '0')}
              </div>
              <div className="construction__card-icon">{service.icon}</div>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
              <div className="construction__card-line" />
              <span className="construction__card-cta">View Details →</span>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="construction__banner"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <div className="construction__banner-content">
            <h3>Ready to Build Your Dream Project?</h3>
            <p>From concept to completion, we bring your vision to life with precision and care.</p>
          </div>
          <a href="#contact" className="construction__banner-btn">
            Start Your Project →
          </a>
        </motion.div>

        <motion.div
          className="construction__stats"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          <div className="construction__stat">
            <span className="construction__stat-number">30+</span>
            <span className="construction__stat-label">Years of Experience</span>
          </div>
          <div className="construction__stat-divider" />
          <div className="construction__stat">
            <span className="construction__stat-number">3,000+</span>
            <span className="construction__stat-label">Happy Customers</span>
          </div>
          <div className="construction__stat-divider" />
          <div className="construction__stat">
            <span className="construction__stat-number">2,000+</span>
            <span className="construction__stat-label">Projects Delivered</span>
          </div>
          <div className="construction__stat-divider" />
          <div className="construction__stat">
            <span className="construction__stat-number">100%</span>
            <span className="construction__stat-label">Client Satisfaction</span>
          </div>
        </motion.div>
      </div>
    </section>

      {/* Service Detail Modal */}
      <AnimatePresence>
        {selected && (
          <motion.div
            className="cd-modal__overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelected(null)}
          >
            <motion.div
              className="cd-modal"
              initial={{ opacity: 0, y: 60, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.96 }}
              transition={{ type: 'spring', stiffness: 300, damping: 28 }}
              onClick={e => e.stopPropagation()}
            >
              <button className="cd-modal__close" onClick={() => setSelected(null)} aria-label="Close">
                <FaTimes />
              </button>

              <div className="cd-modal__header">
                <div className="cd-modal__icon">{selected.icon}</div>
                <h2 className="cd-modal__title">{selected.title}</h2>
              </div>

              <p className="cd-modal__overview">{selected.details.overview}</p>

              <div className="cd-modal__body">
                <div className="cd-modal__section">
                  <h4 className="cd-modal__section-title">Materials We Use</h4>
                  <div className="cd-modal__tags">
                    {selected.details.materials.map((m, i) => (
                      <span key={i} className="cd-modal__tag">{m}</span>
                    ))}
                  </div>
                </div>

                <div className="cd-modal__section">
                  <h4 className="cd-modal__section-title">What's Included</h4>
                  <ul className="cd-modal__list">
                    {selected.details.includes.map((item, i) => (
                      <li key={i} className="cd-modal__list-item">
                        <span className="cd-modal__check">✓</span> {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="cd-modal__timeline">
                  <span className="cd-modal__timeline-label">Typical Timeline</span>
                  <span className="cd-modal__timeline-value">{selected.details.timeline}</span>
                </div>
              </div>

              <div className="cd-modal__actions">
                <a href="tel:+94761202374" className="cd-modal__btn cd-modal__btn--call">
                  <FaPhone /> Call Us
                </a>
                <a href={`https://wa.me/94761202374?text=Hi, I'm interested in your ${encodeURIComponent(selected.title)} service.`} target="_blank" rel="noopener noreferrer" className="cd-modal__btn cd-modal__btn--whatsapp">
                  <FaWhatsapp /> WhatsApp Us
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ConstructionDivision;
